import { useCallback, useMemo, useRef, useState } from "react";
import { matchPath, useLocation } from "react-router-dom";
import { Api } from "../../common/service/Api";
import { RamsayPageContextDto } from "../../common/service/server/dto/RamsayDto";
import { OrganizationPublicId } from "../../common/service/server/Types";
import { Routes } from "../root/routes";

export interface RamsayChatMessage {
  id: string;
  role: "user" | "assistant";
  text: string;
  isError?: boolean;
}

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const RESERVED_REQUEST_SEGMENTS = [
  "active",
  "closed",
  "pending",
  "tutorials",
  "privacy-emails",
  "privacy-email-settings",
];
const ERROR_MESSAGE = "Sorry, something went wrong. Please try again.";
const WARM_THROTTLE_MS = 10 * 60 * 1000;

const buildPageContext = (pathname: string): RamsayPageContextDto => {
  const page: RamsayPageContextDto = { pathname, title: document.title };

  const vendor = matchPath<{ vendorId: string }>(pathname, {
    path: Routes.vendors.detail,
    exact: true,
  });
  if (vendor && UUID_REGEX.test(vendor.params.vendorId)) {
    return { ...page, entityType: "vendor", entityId: vendor.params.vendorId };
  }

  const dataRecipient = matchPath<{ vendorId: string }>(pathname, {
    path: Routes.dataRecipients.detail,
  });
  if (dataRecipient && UUID_REGEX.test(dataRecipient.params.vendorId)) {
    return { ...page, entityType: "data-recipient", entityId: dataRecipient.params.vendorId };
  }

  const request = matchPath<{ requestId: string }>(pathname, {
    path: Routes.requests.detail,
    exact: true,
  });
  if (request && !RESERVED_REQUEST_SEGMENTS.includes(request.params.requestId)) {
    return { ...page, entityType: "request", entityId: request.params.requestId };
  }

  return page;
};

const getOrCreateConversationId = (organizationId: OrganizationPublicId): string => {
  const key = `ramsay-chat-conversation-${organizationId}`;
  const existing = localStorage.getItem(key);
  if (existing && UUID_REGEX.test(existing)) {
    return existing;
  }
  const conversationId = crypto.randomUUID();
  localStorage.setItem(key, conversationId);
  return conversationId;
};

export const useRamsayChat = (organizationId: OrganizationPublicId | undefined) => {
  const location = useLocation();
  const [messages, setMessages] = useState<RamsayChatMessage[]>([]);
  // isSending covers the whole round trip; the typing indicator is derived downstream from
  // the assistant-ui runtime (isRunning with no assistant message yet).
  const [isSending, setIsSending] = useState(false);

  const conversationId = useMemo(
    () => (organizationId ? getOrCreateConversationId(organizationId) : undefined),
    [organizationId],
  );

  const appendMessage = useCallback((message: Omit<RamsayChatMessage, "id">) => {
    setMessages((prev) => [...prev, { ...message, id: crypto.randomUUID() }]);
  }, []);

  // Sets the assistant bubble for the in-flight response, creating it on the first delta.
  const upsertAssistantMessage = useCallback(
    (id: string, buildText: (prevText: string) => string) => {
      setMessages((prev) => {
        const existing = prev.find((message) => message.id === id);
        if (!existing) {
          return [...prev, { id, role: "assistant" as const, text: buildText("") }];
        }
        return prev.map((message) =>
          message.id === id ? { ...message, text: buildText(message.text) } : message,
        );
      });
    },
    [],
  );

  // The conversation's backing VM idles out server-side after ~15 minutes, so re-warming on
  // every panel open is useful but doesn't need to happen more often than this.
  const lastWarmAtRef = useRef(0);
  const warm = useCallback(() => {
    if (!organizationId || !conversationId) {
      return;
    }
    const now = Date.now();
    if (now - lastWarmAtRef.current < WARM_THROTTLE_MS) {
      return;
    }
    lastWarmAtRef.current = now;
    Api.ramsay.warm(organizationId, conversationId);
  }, [organizationId, conversationId]);

  const send = useCallback(
    (text: string) => {
      const message = text.trim();
      if (!message || !organizationId || !conversationId || isSending) {
        return;
      }
      appendMessage({ role: "user", text: message });
      setIsSending(true);

      const assistantId = crypto.randomUUID();
      Api.ramsay
        .streamMessage(
          organizationId,
          { conversationId, message, page: buildPageContext(location.pathname) },
          (delta) => {
            upsertAssistantMessage(assistantId, (prevText) => prevText + delta);
          },
        )
        .then((response) => {
          // Reconcile with the authoritative full text from the terminal "done" event.
          upsertAssistantMessage(assistantId, () => response.text);
        })
        .catch(() => {
          // Any partial text that already streamed in stays visible above the error bubble.
          appendMessage({ role: "assistant", text: ERROR_MESSAGE, isError: true });
        })
        .finally(() => {
          setIsSending(false);
        });
    },
    [
      organizationId,
      conversationId,
      isSending,
      appendMessage,
      upsertAssistantMessage,
      location.pathname,
    ],
  );

  return { messages, send, warm, isSending };
};
